import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MapPin, Store } from 'lucide-react';
import { useLocation } from '@/contexts/LocationContext';
import type { Location } from '@/contexts/LocationContext';

interface LocationSelectionPromptProps {
  locations: Location[];
  title?: string;
  description?: string;
}

export function LocationSelectionPrompt({
  locations,
  title,
  description,
}: LocationSelectionPromptProps) {
  const { setSelectedLocation } = useLocation();
  const { t } = useTranslation('common');

  const displayTitle = title ?? t('locationPrompt.title');
  const displayDescription = description ?? t('locationPrompt.description');

  const handleLocationSelect = (location: Location) => {
    setSelectedLocation(location);
  };

  return (
    <div className="flex-1 flex flex-col items-center justify-center p-4 bg-background">
      <Card className="max-w-2xl w-full">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="p-4 rounded-full bg-primary/10">
              <Store className="h-12 w-12 text-primary" aria-hidden="true" />
            </div>
          </div>
          <CardTitle className="text-2xl">{displayTitle}</CardTitle>
          <CardDescription className="text-base">{displayDescription}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {locations.map((location) => (
              <Button
                key={location.id}
                variant="outline"
                className="h-auto p-4 flex flex-col items-start gap-2 hover:bg-primary/5 hover:border-primary"
                onClick={() => handleLocationSelect(location)}
              >
                <div className="flex items-center gap-2 w-full">
                  <MapPin className="h-5 w-5 text-primary flex-shrink-0" aria-hidden="true" />
                  <span className="font-semibold text-left">{location.name}</span>
                </div>
                {location.city && (
                  <span className="text-sm text-muted-foreground text-left w-full">
                    {location.city}
                  </span>
                )}
              </Button>
            ))}
          </div>

          <p className="text-xs text-muted-foreground text-center mt-6">
            {t('locationPrompt.changeHint')}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
